// src/contexts/GradeContext.js - Safer version
import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useMemo,
	useCallback,
} from "react";

const GradeContext = createContext();

export const GradeProvider = ({ children }) => {
	const [selectedGrade, setSelectedGrade] = useState(null);
	const [isInitialized, setIsInitialized] = useState(false);

	// Initialize from localStorage after component mounts (client-side only)
	useEffect(() => {
		try {
			const savedGrade = localStorage.getItem("selectedGrade");
			if (savedGrade && savedGrade !== "null") {
				setSelectedGrade(savedGrade);
			}
		} catch (error) {
			console.warn("Failed to read from localStorage:", error);
		} finally {
			setIsInitialized(true);
		}
	}, []);

	// Update localStorage when grade changes (only after initialization)
	useEffect(() => {
		if (isInitialized) {
			try {
				if (selectedGrade) {
					localStorage.setItem("selectedGrade", selectedGrade);
				} else {
					localStorage.removeItem("selectedGrade");
				}
			} catch (error) {
				console.warn("Failed to write to localStorage:", error);
			}
		}
	}, [selectedGrade, isInitialized]);

	// Memoize the update function
	const updateSelectedGrade = useCallback((gradeId) => {
		setSelectedGrade(gradeId);
	}, []);

	// Memoize the context value
	const contextValue = useMemo(
		() => ({
			selectedGrade,
			updateSelectedGrade,
			isInitialized,
		}),
		[selectedGrade, updateSelectedGrade, isInitialized]
	);

	return (
		<GradeContext.Provider value={contextValue}>
			{children}
		</GradeContext.Provider>
	);
};

export const useGrade = () => {
	const context = useContext(GradeContext);
	if (!context) {
		throw new Error("useGrade must be used within a GradeProvider");
	}
	return context;
};
